import { useState, useEffect, useRef } from 'react';
import '../Styles/joinModel.css';

const API_BASE_URL = '';

const MAX_IMAGE_MB = 10;
const MAX_IMAGE_BYTES = MAX_IMAGE_MB * 1024 * 1024;

const STEPS = [
  'Personal Information',
  'Professional Information',
  'Location Details',
  'Membership Details',
];

const INDUSTRY_OPTIONS = [
  'Information Technology / IT Services',
  'Manufacturing',
  'Banking, Financial Services & Insurance',
  'Healthcare & Pharmaceuticals',
  'Education',
  'Consulting & Professional Services',
  'Retail & E-commerce',
  'Automotive',
  'GCC / Shared Services',
  'Staffing & Recruitment',
  'Agriculture & AgriTech',
  'Logistics & Supply Chain',
  'Hospitality & Tourism',
  'NGO / Non-Profit',
  'Food & Beverage',
  'Chemicals',
  'Oil & Gas',
  'Construction & Real Estate',
  'Government / Public Sector',
  'Other',
];

const STATE_OPTIONS = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
];

const COUNTRY_OPTIONS = [
  'India',
  'United States',
  'United Kingdom',
  'United Arab Emirates',
  'Singapore',
  'Australia',
  'Canada',
  'Malaysia',
];

const COUNTRY_CODES = ['+91', '+1', '+44', '+971', '+65', '+61'];

const EXPERIENCE_OPTIONS = [
  '0 - 2 years',
  '3 - 5 years',
  '6 - 10 years',
  '11 - 15 years',
  '16 - 20 years',
  '20+ years',
];

const MEMBERSHIP_CATEGORY_OPTIONS = [
  'Elite HR Circle',
  'Future Leaders Club',
  'Educational Club',
  'Founders Club',
  'Consultants & Advisory Club',
];

// Grey text shown under the dropdown. It changes when a category is chosen.
// Change the wording of any line below to whatever you want.
const MEMBERSHIP_CATEGORY_HINTS = {
  'Elite HR Circle': '(Elite HR Circle)',
  'Future Leaders Club': '(Future Leaders Club)',
  'Educational Club': '(Educational Club)',
  'Founders Club': '(Founders Club)',
  'Consultants & Advisory Club': '(Consultants & Advisory Club)',
};

const REASON_OPTIONS = [
  'HR Networking & Community',
  'HR Summits & Conferences',
  'CHRO Roundtables & Leadership Forums',
  'HR Certifications & Professional Development',
  'HR Awards & Recognition',
  'HR Technology & AI',
  'Career Growth & Mentorship',
  'Speaking Opportunities & Thought Leadership',
  'Corporate Partnerships & Business Networking',
  'Campus & Industry Connect',
];

const REFERRAL_OPTIONS = [
  'LinkedIn',
  'Website',
  'HR Event / Conference',
  'Colleague / Friend',
  'Corporate Partner',
  'Other',
];

const initialFormData = {
  // step 1
  fullName: '',
  gender: '',
  countryCode: '+91',
  contactNumber: '',
  personalEmail: '',
  officialEmail: '',
  linkedin: '',
  image: null,
  // step 2
  designation: '',
  company: '',
  industry: '',
  experience: '',
  // step 3
  country: 'India',
  state: '',
  city: '',
  // step 4
  membershipCategory: '',
  reason: '',
  referral: '',
  updates: 'yes',
  accuracy: false,
  privacy: false,
  consent: false,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* ---------------- small helpers ---------------- */
function Req() {
  return <span className="jm-req"> *</span>;
}

function Field({ label, htmlFor, error, hint, children, className = '' }) {
  return (
    <div className={`jm-field ${className}`}>
      <label className="jm-label" htmlFor={htmlFor}>
        {label}
        <Req />
      </label>
      {children}
      {hint && !error && <p className="jm-hint">{hint}</p>}
      {error && <p className="jm-error">{error}</p>}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/* Type-to-filter dropdown (Industry, Country, State, City).
   allowCustom = user can type a value that is not in the list. */
function SearchableDropdown({ id, options, value, onSelect, placeholder, hasError, allowCustom = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const wrapperRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        if (!allowCustom) setQuery(value || '');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value, allowCustom]);

  const filteredOptions = query
    ? options.filter((opt) => opt.toLowerCase().startsWith(query.toLowerCase()))
    : options;

  const handleInputChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setIsOpen(true);

    if (allowCustom) {
      onSelect(v);
      return;
    }
    if (v === '') {
      onSelect('');
      return;
    }
    const exact = options.find((o) => o.toLowerCase() === v.toLowerCase());
    if (exact) onSelect(exact);
  };

  const handleSelect = (label) => {
    onSelect(label);
    setQuery(label);
    setIsOpen(false);
  };

  const showList = isOpen && (filteredOptions.length > 0 || !allowCustom);

  return (
    <div className="jm-dd" ref={wrapperRef}>
      <div className={`jm-dd-trigger ${hasError ? 'jm-invalid' : ''}`}>
        <input
          id={id}
          type="text"
          className="jm-dd-input"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          autoComplete="off"
        />
        <span
          className={`jm-dd-arrow ${isOpen ? 'open' : ''}`}
          onClick={() => setIsOpen((prev) => !prev)}
        />
      </div>

      {showList && (
        <ul className="jm-dd-list" role="listbox">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((label) => (
              <li
                key={label}
                role="option"
                aria-selected={value === label}
                className={`jm-dd-option ${value === label ? 'selected' : ''}`}
                onClick={() => handleSelect(label)}
              >
                {label}
              </li>
            ))
          ) : (
            <li className="jm-dd-option jm-dd-empty">No matches found</li>
          )}
        </ul>
      )}
    </div>
  );
}

// `onMemberAdded` is optional: pass it from the parent page if that page
// also renders the members list and you want it to refresh instantly.
function JoinModal({ isOpen, onClose, onMemberAdded }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const bodyRef = useRef(null);

  /* lock page scroll while open + ESC to close */
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /* scroll body to top on step change */
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [step]);

  if (!isOpen) return null;

  const percent = step * 25;

  const setField = (name, value) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'country') {
        next.state = '';
        next.city = '';
      }
      if (name === 'state') {
        next.city = '';
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setField(name, type === 'checkbox' ? checked : value);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    if (file) setField('image', file);
    e.target.value = '';
  };

  const validate = (s, data) => {
    const err = {};

    if (s === 1) {
      if (!/^[A-Za-z' -]{2,100}$/.test(data.fullName.trim())) {
        err.fullName = 'Please enter your full name.';
      }
      if (!data.gender) {
        err.gender = 'Please select your gender.';
      }
      const mobileOk =
        data.countryCode === '+91'
          ? /^[0-9]{10}$/.test(data.contactNumber.trim())
          : /^[0-9]{10,15}$/.test(data.contactNumber.trim());
      if (!mobileOk) {
        err.contactNumber =
          data.countryCode === '+91'
            ? 'Please enter a valid 10-digit mobile number.'
            : 'Please enter a valid contact number (10–15 digits).';
      }
      if (!EMAIL_RE.test(data.personalEmail) || data.personalEmail.length > 254) {
        err.personalEmail = 'Please enter a valid personal email address.';
      }
      if (
        !EMAIL_RE.test(data.officialEmail) ||
        data.officialEmail.length > 254 ||
        (data.officialEmail &&
          data.personalEmail &&
          data.officialEmail.toLowerCase() === data.personalEmail.toLowerCase())
      ) {
        err.officialEmail = 'Please enter a valid official email address.';
      }
      if (!/^https:\/\/(www\.)?linkedin\.com\//.test(data.linkedin.trim())) {
        err.linkedin = 'Please enter a valid LinkedIn profile URL.';
      }
      if (!data.image) {
        err.image = `Please upload a JPG, PNG or WebP image (maximum ${MAX_IMAGE_MB} MB).`;
      } else {
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(data.image.type) || data.image.size > MAX_IMAGE_BYTES) {
          err.image = `Please upload a JPG, PNG or WebP image (maximum ${MAX_IMAGE_MB} MB).`;
        }
      }
    }

    if (s === 2) {
      if (!/^[A-Za-z0-9&/.\-() ]{2,100}$/.test(data.designation.trim())) {
        err.designation = 'Please enter your designation.';
      }
      if (!/^[A-Za-z0-9&.,'\- ]{2,150}$/.test(data.company.trim())) {
        err.company = 'Please enter your organization name.';
      }
      if (!data.industry || !INDUSTRY_OPTIONS.includes(data.industry)) {
        err.industry = 'Please select an industry.';
      }
      if (!data.experience) {
        err.experience = 'Please select your experience.';
      }
    }

    if (s === 3) {
      if (!data.country.trim()) {
        err.country = 'Please enter your country.';
      }
      if (data.country.trim().toLowerCase() === 'india') {
        if (!data.state || !STATE_OPTIONS.includes(data.state)) {
          err.state = 'Please select your state.';
        }
      } else if (!data.state.trim()) {
        err.state = 'Please enter your state.';
      }
      if (!/^[A-Za-z.\- ]{2,100}$/.test(data.city.trim())) {
        err.city = 'Please enter your city.';
      }
    }

    if (s === 4) {
      if (!data.membershipCategory) err.membershipCategory = 'Please select a membership category.';
      if (!data.reason) err.reason = 'Please select a reason.';
      if (!data.referral) err.referral = 'Please select a referral source.';
      if (!data.accuracy) err.accuracy = 'Please confirm the information accuracy.';
      if (!data.privacy) err.privacy = 'Please accept the Privacy Policy & Terms.';
      if (!data.consent) err.consent = 'Please provide communication consent.';
    }

    return err;
  };

  const submitApplication = async () => {
    setSubmitting(true);
    setSubmitError('');

    try {
      // multipart/form-data so the image file travels with the rest of the fields
      const payload = new FormData();

      // fields your backend already expects (names unchanged)
      payload.append('fullName', formData.fullName.trim());
      payload.append('designation', formData.designation.trim());
      payload.append('company', formData.company.trim());
      payload.append('contact', formData.contactNumber.trim());
      payload.append('personalEmail', formData.personalEmail.trim());
      payload.append('officialEmail', formData.officialEmail.trim());
      payload.append('industry', formData.industry);
      payload.append('gender', formData.gender);
      payload.append('linkedIn', formData.linkedin.trim());
      payload.append('state', formData.state.trim());
      payload.append('city', formData.city.trim());
      if (formData.image) {
        payload.append('image', formData.image);
      }

      // new fields from the 4-step form
      payload.append('countryCode', formData.countryCode);
      payload.append('country', formData.country.trim());
      payload.append('experience', formData.experience);
      payload.append('membershipCategory', formData.membershipCategory);
      payload.append('reason', formData.reason);
      payload.append('referralSource', formData.referral);
      payload.append('receiveUpdates', formData.updates === 'yes' ? 'true' : 'false');
      payload.append('accuracyConfirmed', String(formData.accuracy));
      payload.append('privacyAccepted', String(formData.privacy));
      payload.append('communicationConsent', String(formData.consent));

      const res = await fetch(`${API_BASE_URL}/api/community-members`, {
        method: 'POST',
        body: payload,
        // Do NOT set Content-Type manually, the browser sets the multipart boundary.
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.message || 'Failed to submit. Please try again.');
      }

      const created = await res.json();

      if (onMemberAdded) {
        onMemberAdded(created);
      }

      setSubmitSuccess(true);

      // Give the person a moment to see the thank-you message, then close.
      setTimeout(() => {
        setFormData(initialFormData);
        setErrors({});
        setStep(1);
        setSubmitSuccess(false);
        onClose();
      }, 2500);
    } catch (err) {
      console.error(err);
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = () => {
    if (submitting || submitSuccess) return;

    const validationErrors = validate(step, formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    if (step < 4) {
      setSubmitError('');
      setStep(step + 1);
    } else {
      submitApplication();
    }
  };

  const handleBack = () => {
    setErrors({});
    setSubmitError('');
    if (step > 1) setStep(step - 1);
  };

  function handleClose() {
    setFormData(initialFormData);
    setErrors({});
    setSubmitError('');
    setSubmitSuccess(false);
    setStep(1);
    onClose();
  }

  const err = (k) => (errors[k] ? 'jm-invalid' : '');
  const isIndia = formData.country.trim().toLowerCase() === 'india';

  return (
    <div
      className="jm-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="jm-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Join the Vanakkam HRD Community"
      >
        {/* ---------------- HEADER (fixed) ---------------- */}
        <div className="jm-head">
          <button type="button" className="jm-close" onClick={handleClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <line x1="5" y1="5" x2="19" y2="19" />
              <line x1="19" y1="5" x2="5" y2="19" />
            </svg>
          </button>

          <h2 className="jm-title">Join the Vanakkam HRD Community.</h2>
          <p className="jm-subtitle">Complete your membership application in four simple steps.</p>

          <div className="jm-progress-row">
            <span className="jm-step-text">Step {step} of 4</span>
            <span className="jm-percent">{percent}%</span>
          </div>
          <div className="jm-track">
            <div className="jm-fill" style={{ width: `${percent}%` }} />
          </div>

          <ol className="jm-stepper">
            {STEPS.map((label, i) => {
              const n = i + 1;
              const state = n < step ? 'done' : n === step ? 'active' : 'todo';
              return (
                <li key={label} className={`jm-stepper-item jm-${state}`}>
                  <span className="jm-circle">{state === 'done' ? <CheckIcon /> : n}</span>
                  <span className="jm-step-label">{label}</span>
                </li>
              );
            })}
          </ol>
        </div>

        {/* ---------------- BODY (scrolls) ---------------- */}
        <div className="jm-body" ref={bodyRef}>
          {submitSuccess ? (
            <div className="jm-success">
              <div className="jm-success-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <p className="jm-success-text">Thanks for joining the community!</p>
            </div>
          ) : (
            <>
              {/* ===== STEP 1 ===== */}
              {step === 1 && (
                <>
                  <h3 className="jm-section-title">Personal Information</h3>
                  <p className="jm-section-sub">Tell us a little about yourself. All fields are required.</p>

                  <div className="jm-grid">
                    <Field label="Full Name" htmlFor="jm-fullName" error={errors.fullName}>
                      <input
                        id="jm-fullName"
                        className={`jm-input ${err('fullName')}`}
                        type="text"
                        name="fullName"
                        placeholder="Enter Your full name"
                        value={formData.fullName}
                        onChange={handleChange}
                      />
                    </Field>

                    <Field label="Gender" htmlFor="jm-gender" error={errors.gender}>
                      <select
                        id="jm-gender"
                        className={`jm-input jm-select ${!formData.gender ? 'jm-empty' : ''} ${err('gender')}`}
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                      >
                        <option value="" disabled>Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </Field>

                    <Field label="Contact Number" htmlFor="jm-contact" error={errors.contactNumber}>
                      <div className={`jm-phone ${err('contactNumber')}`}>
                        <select
                          className="jm-code"
                          name="countryCode"
                          value={formData.countryCode}
                          onChange={handleChange}
                          aria-label="Country code"
                        >
                          {COUNTRY_CODES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                        <span className="jm-phone-divider" />
                        <input
                          id="jm-contact"
                          className="jm-phone-input"
                          type="tel"
                          name="contactNumber"
                          inputMode="numeric"
                          maxLength={15}
                          placeholder="Mobile number"
                          value={formData.contactNumber}
                          onChange={(e) => setField('contactNumber', e.target.value.replace(/\D/g, ''))}
                        />
                      </div>
                    </Field>

                    <Field label="Personal Email ID" htmlFor="jm-personalEmail" error={errors.personalEmail}>
                      <input
                        id="jm-personalEmail"
                        className={`jm-input ${err('personalEmail')}`}
                        type="email"
                        name="personalEmail"
                        placeholder="Enter your Personal Email ID"
                        maxLength={254}
                        value={formData.personalEmail}
                        onChange={handleChange}
                      />
                    </Field>

                    <Field label="Official Email ID" htmlFor="jm-officialEmail" error={errors.officialEmail}>
                      <input
                        id="jm-officialEmail"
                        className={`jm-input ${err('officialEmail')}`}
                        type="email"
                        name="officialEmail"
                        placeholder="Enter your Official Email ID"
                        maxLength={254}
                        value={formData.officialEmail}
                        onChange={handleChange}
                      />
                    </Field>

                    <Field label="LinkedIn Profile URL" htmlFor="jm-linkedin" error={errors.linkedin}>
                      <input
                        id="jm-linkedin"
                        className={`jm-input ${err('linkedin')}`}
                        type="url"
                        name="linkedin"
                        placeholder="https://linkedin.com/in/your-name"
                        value={formData.linkedin}
                        onChange={handleChange}
                      />
                    </Field>

                    <Field label="Upload Your Image" htmlFor="jm-upload" error={errors.image} className="jm-full">
                      {/* the whole box is a label, so clicking anywhere inside opens the file picker */}
                      <label htmlFor="jm-upload" className={`jm-upload ${err('image')}`}>
                        <div className="jm-upload-left">
                          <img
                            src="/assets/jc_icn_05.png"
                            alt="Upload"
                            className="jm-upload-icon"
                          />
                          <div className="jm-upload-text">
                            <p className="jm-upload-title">
                              {formData.image ? formData.image.name : 'Choose an image'}
                            </p>
                            <p className="jm-upload-sub">JPG or PNG · Maximum {MAX_IMAGE_MB} MB</p>
                          </div>
                        </div>

                        <span className="jm-browse">Browse</span>

                        <input
                          id="jm-upload"
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                          onChange={handleFileChange}
                          hidden
                        />
                      </label>
                    </Field>
                  </div>
                </>
              )}

              {/* ===== STEP 2 ===== */}
              {step === 2 && (
                <>
                  <h3 className="jm-section-title">Professional Information</h3>
                  <p className="jm-section-sub">Help us understand your professional background.</p>

                  <div className="jm-grid">
                    <Field label="Current Designation" htmlFor="jm-designation" error={errors.designation}>
                      <input
                        id="jm-designation"
                        className={`jm-input ${err('designation')}`}
                        type="text"
                        name="designation"
                        placeholder="Enter your Designation"
                        value={formData.designation}
                        onChange={handleChange}
                      />
                    </Field>

                    <Field label="Organization Name" htmlFor="jm-company" error={errors.company}>
                      <input
                        id="jm-company"
                        className={`jm-input ${err('company')}`}
                        type="text"
                        name="company"
                        placeholder="Enter your Organization Name"
                        value={formData.company}
                        onChange={handleChange}
                      />
                    </Field>

                    <Field
                      label="Nature of Industry"
                      htmlFor="jm-industry"
                      error={errors.industry}
                      hint="Search by industry name or choose Other."
                      className="jm-full"
                    >
                      <SearchableDropdown
                        id="jm-industry"
                        options={INDUSTRY_OPTIONS}
                        value={formData.industry}
                        onSelect={(label) => setField('industry', label)}
                        placeholder="Select Your Nature of Industry"
                        hasError={!!errors.industry}
                      />
                    </Field>

                    <Field
                      label="Total Professional Experience"
                      htmlFor="jm-experience"
                      error={errors.experience}
                      className="jm-full"
                    >
                      <select
                        id="jm-experience"
                        className={`jm-input jm-select ${!formData.experience ? 'jm-empty' : ''} ${err('experience')}`}
                        name="experience"
                        value={formData.experience}
                        onChange={handleChange}
                      >
                        <option value="" disabled>Select your Experience</option>
                        {EXPERIENCE_OPTIONS.map((x) => (
                          <option key={x} value={x}>{x}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </>
              )}

              {/* ===== STEP 3 ===== */}
              {step === 3 && (
                <>
                  <h3 className="jm-section-title">Location Details</h3>
                  <p className="jm-section-sub">Connect with the community closest to you.</p>

                  <div className="jm-grid">
                    <Field
                      label="Country"
                      htmlFor="jm-country"
                      error={errors.country}
                      hint="Search or enter your country."
                      className="jm-full"
                    >
                      <SearchableDropdown
                        id="jm-country"
                        options={COUNTRY_OPTIONS}
                        value={formData.country}
                        onSelect={(label) => setField('country', label)}
                        placeholder="Select or enter your country"
                        hasError={!!errors.country}
                        allowCustom
                      />
                    </Field>

                    <Field
                      label="State"
                      htmlFor="jm-state"
                      error={errors.state}
                      hint="Search or enter your state."
                    >
                      <SearchableDropdown
                        id="jm-state"
                        options={isIndia ? STATE_OPTIONS : []}
                        value={formData.state}
                        onSelect={(label) => setField('state', label)}
                        placeholder="Select or enter your state"
                        hasError={!!errors.state}
                        allowCustom={!isIndia}
                      />
                    </Field>

                    <Field
                      label="City"
                      htmlFor="jm-city"
                      error={errors.city}
                      hint="Search or enter your city."
                    >
                      <SearchableDropdown
                        id="jm-city"
                        options={[]}
                        value={formData.city}
                        onSelect={(label) => setField('city', label)}
                        placeholder="Select or enter your city"
                        hasError={!!errors.city}
                        allowCustom
                      />
                    </Field>
                  </div>
                </>
              )}

              {/* ===== STEP 4 ===== */}
              {step === 4 && (
                <>
                  <h3 className="jm-section-title">Membership Details</h3>
                  <p className="jm-section-sub">Tell us what brings you here, then confirm your preferences.</p>

                  <div className="jm-grid">
                    <Field
                      label="Membership Category"
                      htmlFor="jm-membershipCategory"
                      error={errors.membershipCategory}
                      hint={MEMBERSHIP_CATEGORY_HINTS[formData.membershipCategory]}
                      className="jm-full"
                    >
                      <select
                        id="jm-membershipCategory"
                        className={`jm-input jm-select ${!formData.membershipCategory ? 'jm-empty' : ''} ${err('membershipCategory')}`}
                        name="membershipCategory"
                        value={formData.membershipCategory}
                        onChange={handleChange}
                      >
                        <option value="" disabled>Select a membership category</option>
                        {MEMBERSHIP_CATEGORY_OPTIONS.map((x) => (
                          <option key={x} value={x}>{x}</option>
                        ))}
                      </select>
                    </Field>

                    <Field
                      label="Primary Reason for Joining Vanakkam HRD"
                      htmlFor="jm-reason"
                      error={errors.reason}
                      className="jm-stack-m"
                    >
                      <select
                        id="jm-reason"
                        className={`jm-input jm-select ${!formData.reason ? 'jm-empty' : ''} ${err('reason')}`}
                        name="reason"
                        value={formData.reason}
                        onChange={handleChange}
                      >
                        <option value="" disabled>Select a reason</option>
                        {REASON_OPTIONS.map((x) => (
                          <option key={x} value={x}>{x}</option>
                        ))}
                      </select>
                    </Field>

                    <Field
                      label="Referral Source"
                      htmlFor="jm-referral"
                      error={errors.referral}
                      className="jm-stack-m"
                    >
                      <select
                        id="jm-referral"
                        className={`jm-input jm-select ${!formData.referral ? 'jm-empty' : ''} ${err('referral')}`}
                        name="referral"
                        value={formData.referral}
                        onChange={handleChange}
                      >
                        <option value="" disabled>Select a source</option>
                        {REFERRAL_OPTIONS.map((x) => (
                          <option key={x} value={x}>{x}</option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <div className="jm-block">
                    <p className="jm-label">
                      Do You Wish to Receive Updates About Upcoming Events and Programs?
                      <Req />
                    </p>
                    <div className="jm-radio-row">
                      <label className="jm-radio">
                        <input type="radio" name="updates" value="yes" checked={formData.updates === 'yes'} onChange={handleChange} />
                        <span className="jm-radio-dot" />
                        <span className="jm-radio-text">Yes</span>
                      </label>
                      <label className="jm-radio">
                        <input type="radio" name="updates" value="no" checked={formData.updates === 'no'} onChange={handleChange} />
                        <span className="jm-radio-dot" />
                        <span className="jm-radio-text">No</span>
                      </label>
                    </div>
                  </div>

                  <div className="jm-block">
                    <p className="jm-label">
                      Information Accuracy
                      <Req />
                    </p>
                    <label className="jm-check">
                      <input type="checkbox" name="accuracy" checked={formData.accuracy} onChange={handleChange} />
                      <span className="jm-box"><CheckIcon /></span>
                      <span className="jm-check-text">
                        I confirm that the information provided in this application is accurate and complete.
                      </span>
                    </label>
                    {errors.accuracy && <p className="jm-error">{errors.accuracy}</p>}
                  </div>

                  <div className="jm-block">
                    <p className="jm-label">
                      Privacy Policy &amp; Terms
                      <Req />
                    </p>
                    <label className="jm-check">
                      <input type="checkbox" name="privacy" checked={formData.privacy} onChange={handleChange} />
                      <span className="jm-box"><CheckIcon /></span>
                      <span className="jm-check-text">
                        I have read and agree to the Vanakkam HRD Privacy Policy and Membership Terms.
                      </span>
                    </label>
                    {errors.privacy && <p className="jm-error">{errors.privacy}</p>}
                  </div>

                  <div className="jm-block">
                    <p className="jm-label">
                      Communication Consent
                      <Req />
                    </p>
                    <label className="jm-check">
                      <input type="checkbox" name="consent" checked={formData.consent} onChange={handleChange} />
                      <span className="jm-box"><CheckIcon /></span>
                      <span className="jm-check-text">
                        I agree to receive membership updates, event invitations and relevant professional
                        communications from Vanakkam HRD.
                      </span>
                    </label>
                    {errors.consent && <p className="jm-error">{errors.consent}</p>}
                  </div>

                  {submitError && <p className="jm-error jm-submit-error">{submitError}</p>}
                </>
              )}
            </>
          )}
        </div>

        {/* ---------------- FOOTER (fixed) ---------------- */}
        {!submitSuccess && (
          <div className="jm-footer">
            <p className="jm-secure">Your information stays private and secure.</p>
            <div className="jm-actions">
              {step > 1 && (
                <button
                  type="button"
                  className="jm-btn jm-btn-back"
                  onClick={handleBack}
                  disabled={submitting}
                >
                  <img
                    src="/assets/jc_icn_06.png"
                    alt=""
                    className="jm-btn-icon"
                  />
                  Back
                </button>
              )}
              <button
                type="button"
                className="jm-btn jm-btn-next"
                onClick={handleContinue}
                disabled={submitting}
              >
                {step === 4 ? (submitting ? 'Submitting...' : 'Submit Application') : 'Continue'}
                {step < 4 && (
                  <img
                    src="/assets/arrow%20_icon_get.png"
                    alt=""
                    className="jm-btn-icon"
                  />
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default JoinModal;